import { forwardRef, type HTMLAttributes, type RefObject } from 'react';
import { cn } from '@virginexperiencedays/tailwind';
import { IconMinus } from '../../icons/IconMinus';
import { IconPlus } from '../../icons/IconPlus';

export const AccordionGroup = ({ children, ...rest }: HTMLAttributes<HTMLDivElement>) => {
  return <div {...rest}>{children}</div>;
};

export const AccordionItem = forwardRef(
  (
    { className, children, ...rest }: HTMLAttributes<HTMLDivElement>,
    ref: RefObject<HTMLDivElement>
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'border-border-neutral dark:border-persistent-text-light border-b border-solid py-6 first:pt-0',
          className
        )}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
AccordionItem.displayName = 'AccordionItem';

type BaseAccordionProps = {
  /**
   * use to show or hide AccordionContent
   */
  isOpen: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const AccordionHeading = ({
  isOpen = false,
  className,
  children,
  ...rest
}: BaseAccordionProps) => {
  return (
    <div className={cn('flex cursor-pointer items-center justify-between', className)} {...rest}>
      {children}
      <div className="text-brand" data-testid="accordion-icon">
        {!isOpen ? (
          <IconPlus data-testid="accordion-icon-plus" />
        ) : (
          <IconMinus data-testid="accordion-icon-minus" />
        )}
      </div>
    </div>
  );
};

/**
 * Takes any children element that can be a component, html element or even plain text.
 */
// children: ReactNode; // added this for storybook description

export const AccordionContent = ({
  isOpen = false,
  className,
  children,
  ...rest
}: BaseAccordionProps) => {
  return (
    <div
      className={cn('overflow-hidden', isOpen ? 'max-h-auto' : 'max-h-[0]', className)}
      aria-expanded={isOpen}
      {...rest}
    >
      <div className="mt-4">{children}</div>
    </div>
  );
};
